import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { Tenant } from '@etiquetachef/types';
import { TenantMapEmbed } from './TenantMapEmbed';
import { OperatingLocationsSection } from './OperatingLocationsSection';
import { CopyValue } from '@/components/CopyValue';

type Props = { tenant: Tenant };

/**
 * Aba "Local" do TenantDetailPage: endereço DECLARADO (placeId, mapa embed
 * centrado no place) e, abaixo, onde o tenant opera DE FATO — as células de
 * posição do aparelho registradas na impressão e na gravação NFC (#996).
 *
 * Origem: sem issue — aba Local do detalhe do tenant; seção "Onde opera" na
 * #996 (2026-10-18 14:17 +0200).
 *
 * Usado por: `routes/TenantDetailPage.tsx`.
 *
 * Dicas: placeId é o canônico (estável) do endereço. O mapa vive no
 * `TenantMapEmbed` (compartilhado com o modal "Ver no Local" e com a seção
 * `OperatingLocationsSection`).
 */
export function TenantLocationTab({ tenant }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const address = tenant.address ?? '';
  const placeId = tenant.placeId ?? null;

  const mono = (v: React.ReactNode): React.JSX.Element => (
    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
  );

  return (
    <Container header={<Header variant="h2">{t('tenantDetail.location.title')}</Header>}>
      <SpaceBetween size="m">
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">{t('tenantDetail.location.address')}</Box>
            <div>{address.length > 0 ? <CopyValue value={address} /> : '—'}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">{t('tenantDetail.location.placeId')}</Box>
            <div style={{ wordBreak: 'break-all' }}>
              {placeId !== null && placeId.length > 0 ? mono(placeId) : '—'}
            </div>
          </div>
        </ColumnLayout>

        <TenantMapEmbed tenant={tenant} />

        <OperatingLocationsSection tenant={tenant} />
      </SpaceBetween>
    </Container>
  );
}
